import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaUserAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkedAlt,
  FaHome,
  FaFilePdf,
  FaFileWord,
  FaBirthdayCake,
  FaSchool,
  FaVenusMars,
  FaTimes,
  FaSpinner,
  FaBuilding,
  FaIdBadge,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { BarLoader } from "react-spinners";
import axios from "axios";

const EditBeneficiary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    kebele: "",
    location: "",
    wereda: "",
    age: "",
    gender: "",
    school: "",
    kfleketema: "",
    houseNo: "",
    registeredTo: "Not assigned",
    birth_certificate_id: "",
  });

  const [files, setFiles] = useState({
    idFile: null,
    birth_certificate: null,
    photo: null,
  });

  const [csoList, setCsoList] = useState([]);
  const [loadingCso, setLoadingCso] = useState(true);

  // Fetch CSO list on component mount
  useEffect(() => {
    const fetchCsoList = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/cso/getAll/cso/data/alls`,
          { withCredentials: true }
        );
        
        // The API returns an array directly, not wrapped in a csoList property
        if (Array.isArray(response.data)) {
          setCsoList(response.data);
        } else {
          toast.error("Failed to fetch CSO list - invalid response format");
          console.log("Invalid response format", response.data);
        }
      } catch (error) {
        console.error("Error fetching CSO list:", error);
        toast.error("Error loading CSO list");
      } finally {
        setLoadingCso(false);
      }
    };

    fetchCsoList();
  }, []);

  // Fetch beneficiary data by ID on mount
  useEffect(() => {
    const fetchBeneficiary = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/beneficiaries/${id}`,
          {
            credentials: "include",
          }
        );

        if (response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login");
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch beneficiary data");
        }

        const data = await response.json();
        // Set registeredTo to "Not assigned" if it's null or empty
        const registeredToValue = data.data.registeredTo || "Not assigned";
        setFormData({
          ...data.data,
          registeredTo: registeredToValue
        });
      } catch (error) {
        console.error("Error fetching beneficiary:", error);
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBeneficiary();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleFileChange = (e) => {
  const file = e.target.files[0];
  const { name } = e.target;

  if (file) {
    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      toast.error(`${name} must be less than ${maxSizeMB}MB`);
      e.target.value = ""; // Reset input
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = new FormData();

    // Append form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "idFile" && key !== "photo" && key !== "birth_certificate" ) {
        data.append(key, value);
      }
    });

    // Append files if available
    Object.entries(files).forEach(([key, file]) => {
      if (file) data.append(key, file);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/beneficiaries/${id}`,
        {
          method: "PUT",
          body: data,
          credentials: "include",
        }
      );

      if (response.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Update failed");
      }

      const result = await response.json();
      if (result.success) {
        toast.success("Beneficiary updated successfully!");
        navigate("/admin/beneficiary_list");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Array for text fields with icons (excluding gender and registeredTo, which are handled separately)
  const textFields = [
    { label: "Full Name", name: "fullName", type: "text", icon: FaUserAlt },
    { label: "Phone", name: "phone", type: "tel", icon: FaPhoneAlt },
    {
      label: "Email(optional)",
      name: "email",
      type: "email",
      icon: FaEnvelope,
      optional: true,
    },
    { label: "Kebele", name: "kebele", type: "text", icon: FaHome },
    { label: "Location", name: "location", type: "text", icon: FaMapMarkedAlt },
    { label: "Wereda", name: "wereda", type: "text", icon: FaMapMarkedAlt },
    { label: "Age", name: "age", type: "number", icon: FaBirthdayCake, min: 0 },
    {
      label: "School(optional)",
      name: "school",
      type: "text",
      icon: FaSchool,
      optional: true,
    },
    {
      label: "Kfleketema",
      name: "kfleketema",
      type: "text",
      icon: FaMapMarkedAlt,
    },
    { label: "House No", name: "houseNo", type: "text", icon: FaHome },
    { label: "Birth Certificate Id", name: "birth_certificate_id", type: "text", icon: FaIdBadge },

  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-transparent">
        <BarLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold text-gray-700 text-center mb-6">
          Edit Beneficiary
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <small className="text-sm text-gray-500">Max file size: 5MB</small>

              <label className="block text-gray-600 font-medium">
                <FaFileWord className="inline mr-2 text-blue-500" />
                Photo
              </label>

              {formData.photo && typeof formData.photo === "string" && (
                <a
                  href={`${process.env.REACT_APP_API_URL}/${formData.photo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline mb-2"
                >
                  View Current Photo
                </a>
              )}
              {files.photo && (
                <span className="text-blue-500 mb-2">
                  {files.photo.name}
                </span>
              )}
              <input
                type="file"
                name="photo"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div className="space-y-2">
              <small className="text-sm text-gray-500">Max file size: 5MB</small>

              <label className="block text-gray-600 font-medium">
                <FaFilePdf className="inline mr-2 text-red-500" />
                ID File
              </label>
              {formData.idFile && typeof formData.idFile === "string" && (
                <a
                  href={`${process.env.REACT_APP_API_URL}/${formData.idFile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline mb-2"
                >
                  View Current File(optional)
                </a>
              )}
              {files.idFile && (
                <span className="text-blue-500 underline block mb-2">
                  {files.idFile.name}
                </span>
              )}
              <input
                type="file"
                name="idFile"
                accept=".pdf,.doc,.docx,image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div className="space-y-2">
              <small className="text-sm text-gray-500">Max file size: 5MB</small>
              <label className="block text-gray-600 font-medium">
                <FaFilePdf className="inline mr-2 text-red-500" />
                Birth Certificate(optional)
              </label>
              {formData.birth_certificate && typeof formData.birth_certificate === "string" && (
                <a
                  href={`${process.env.REACT_APP_API_URL}/${formData.birth_certificate}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline mb-2"
                >
                  View Current File
                </a>
              )}
              {files.birth_certificate && (
                <span className="text-blue-500 underline block mb-2">
                  {files.birth_certificate.name}
                </span>
              )}
              <input
                type="file"
                name="birth_certificate"
                accept=".pdf,image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          {/* CSO Selection */}
          <div className="relative">
            <label className="block text-gray-600 font-medium mb-2">
              Assign to CSO <span className="text-red-500">*</span>
            </label>
            <FaBuilding className="absolute left-3 top-10 text-blue-800" />
            {loadingCso ? (
              <div className="flex items-center justify-center py-2">
                <FaSpinner className="animate-spin mr-2" />
                Loading CSOs...
              </div>
            ) : (
              <select
                name="registeredTo"
                value={formData.registeredTo}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Not assigned">Not assigned</option>
                {csoList.map((cso) => (
                  <option key={cso.id} value={cso.id}>
                    {cso.csoName} ({cso.registrationId})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Text Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {textFields.map(
              ({ label, name, type, icon: Icon, min, optional }) => (
                <div className="relative" key={name}>
                  <label className="block text-gray-600 font-medium mb-2">
                    {label}
                    {!optional && <span className="text-red-500">*</span>}
                  </label>
                  <Icon className="absolute left-3 top-11 text-blue-800" />
                  <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    className="pl-10 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required={!optional}
                    {...(min !== undefined ? { min } : {})}
                  />
                </div>
              )
            )}
            {/* Gender Select Field */}
            <div className="relative">
              <label className="block text-gray-600 font-medium mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <FaVenusMars className="absolute left-3 top-10 text-blue-800" />
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="pl-10 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/admin/beneficiary_list")}
              className="flex items-center justify-center w-32 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-200"
            >
              <FaTimes className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center justify-center w-44 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:bg-blue-300"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                "Update Beneficiary"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBeneficiary;